import { Lightning01 } from '@untitled-ui/icons-react';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { CheckboxField } from './checkbox-field';
import { FeaturedIcon } from './featured-icon';

export type BlogGenerationCardProps = {
  title: string;
  subtitle: string;
  icon?: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: () => void;
  disabled?: boolean;
  featuredIconColor?:
    | 'gray'
    | 'red'
    | 'yellow'
    | 'green'
    | 'accent'
    | 'orange'
    | 'accent-contrast'
    | 'accent-darker';
  className?: string;
};

export const BlogGenerationCard = forwardRef<HTMLDivElement, BlogGenerationCardProps>(
  function BlogGenerationCard(props, ref) {
    const {
      title,
      subtitle,
      icon,
      checked = false,
      onCheckedChange,
      onClick,
      disabled = false,
      featuredIconColor = 'accent',
      className = '',
    } = props;

    function handleCardClick() {
      if (disabled) {
        return;
      }
      if (onClick != null) {
        onClick();
      }
      if (onCheckedChange != null) {
        onCheckedChange(!checked);
      }
      return;
    }

    function handleCheckboxChange(newChecked: boolean) {
      if (disabled) {
        return;
      }
      if (onCheckedChange != null) {
        onCheckedChange(newChecked);
      }
    }

    return (
      <div
        ref={ref}
        className={twMerge(
          'duration-250 group relative flex min-w-[200px] flex-1 cursor-pointer flex-col gap-4 rounded-xl border-2 bg-white p-4 transition-all',
          checked
            ? 'border-branding-accent-600'
            : 'border-branding-accent-200 hover:border-branding-accent',
          disabled ? 'pointer-events-none opacity-50' : '',
          className
        )}
        onClick={handleCardClick}
      >
        {/* Checkbox in top-right corner */}
        <div
          className="absolute right-4 top-4"
          onClick={function (e) {
            e.stopPropagation();
          }}
        >
          <CheckboxField
            size="md"
            checked={checked}
            onCheckedChange={handleCheckboxChange}
            disabled={disabled}
          />
        </div>

        {/* Featured Icon */}
        <div className="flex items-start gap-2.5">
          <FeaturedIcon
            icon={icon ?? <Lightning01 />}
            size="xs"
            color={featuredIconColor}
            border={true}
          />
          <p className="text-branding-black font-display-4">{title}</p>
        </div>

        {/* Title and Subtitle */}
        <div className="flex flex-col gap-1">
          <p className="font-body-2-r text-dark-gray">{subtitle}</p>
        </div>
      </div>
    );
  }
);

BlogGenerationCard.displayName = 'BlogGenerationCard';
